import { useContext, useEffect, useMemo, useRef } from "react";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { HealthViewFiltersContext } from "../../pages/ExploreHealth";
import { HEALTH_DATA_TPID_CACHE_KEY, useGetHealthDataByTPIDMutation } from "../../slices/ExploreHealth.slice";
import { HealthOverviewAndSummaryMutationContext } from "./HealthView";
import { HealthViewTabsContainer } from "./Containers/HealthViewTabsContainer";

export default function HealthViewTPID() {
  const [getData, { data, error, isLoading, isError, isSuccess }] = useGetHealthDataByTPIDMutation({
    fixedCacheKey: HEALTH_DATA_TPID_CACHE_KEY
  });
  const filters = useContext(HealthViewFiltersContext);
  // Track last fetched filter values to avoid redundant API calls on remount
  const lastFetchedFiltersRef = useRef<string>("");
  const mutationValue = useMemo(
    () => ({
      data: {
        healthDataList: data?.healthDataList || [],
        healthDataKustoLink: data?.healthDataKustoLink || "",
        healthDataByCustomerHealthScoreStatusKustoLink: data?.healthDataByCustomerHealthScoreStatusKustoLink,
        healthDataBySupportHealthScoreStatusKustoLink: data?.healthDataBySupportHealthScoreStatusKustoLink,
        healthDataByE5RenewalTimeframeKustoLink: data?.healthDataByE5RenewalTimeframeKustoLink,
      },
      isError: isError,
      isLoading: isLoading,
      viewMode: "tpid" as const,
    }),
    [data, isError, isLoading]
  );

  useEffect(() => {
    const customProps = {
      productFamily: filters?.productFamily,
      region: filters?.region,
      industry: filters?.industry,
      segmentGroup: filters?.segmentGroup,
      segment: filters?.segment,
      subSegment: filters?.subSegment,
      isS500: filters?.isS500,
      initiatives: filters?.initiatives
    };
    if (isSuccess) {
      logEvent("useGetHealthDataByTPIDMutation API success", "Health Page", "HealthViewTPID", customProps);
    }
    if (isError) {
      logException("Health Page", "HealthViewTPID", "useGetHealthDataByTPIDMutation", error, { HealthViewFilters: filters });
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    const requestData = {
      productFamily: filters.productFamily,
      region: filters.region,
      industry: filters.industry,
      segmentGroup: filters.segmentGroup,
      segment: filters.segment,
      subSegment: filters.subSegment,
      isS500: filters.isS500,
      initiatives: filters.initiatives,
      subInitiatives: filters.subInitiatives,
      agreementEndQuarter: filters.agreementEndQuarter
    };

    // Create a fingerprint of current filters to detect changes
    const filterFingerprint = JSON.stringify(requestData);

    // Only fetch if filters changed or no data exists yet
    if (filterFingerprint !== lastFetchedFiltersRef.current || !data) {
      lastFetchedFiltersRef.current = filterFingerprint;
      getData(requestData);
    }
  }, [
    filters.productFamily,
    filters.industry,
    filters.region,
    filters.segmentGroup,
    filters.segment,
    filters.subSegment,
    filters.isS500,
    filters.initiatives,
    filters.subInitiatives,
    filters.agreementEndQuarter,
    getData,
    data
  ]);

  return (
    <div style={{ paddingBottom: 10, width: "100%", marginTop: 10 }}>
      <HealthOverviewAndSummaryMutationContext.Provider value={mutationValue}>
        <HealthViewTabsContainer />
      </HealthOverviewAndSummaryMutationContext.Provider>
    </div>
  );
}
